import { create } from 'zustand'
import axios from 'axios'

// API base URL
export const API_SLUG = `${process.env.REACT_APP_BACKEND_DASHBOARD_API_URL}`

// Chart endpoints
const ENDPOINTS = {
  SUCCESSFUL: '/piles/successful',
  UNSUCCESSFUL: '/piles/unsuccessful',
  ALL: '/piles/all',
  STACKED: '/piles/stacked',
  ISSUE: '/maintenance/categories',
  STATUS: '/projects/progress',
  JOB_STATISTICS: '/piles/job-stats',
  DIVISION_LIST: '/sale-order/divisions',

  PROJECT_STATS: '/projects/stats',
  PROJECT_PILE_STATS: '/projects/pile/stats',
  PROJECT_PILE_HOLES_UPDATE_THIS_WEEK: '/projects/pile-holes/updates-this-week',
  PROJECT_PROGRESSION_BY_MACHINE: '/projects/progression-by-machine',
}

export interface ProjectStatusData {
  projects: string[]
  data: Array<{
    name: string
    data: Array<number | null>
  }>
}

export interface ChartStore {
  successfulData: any[]
  unsuccessfulData: any[]
  allData: any[]
  stackedData: any[]
  issueData: any[]
  comparisonData: ProjectStatusData
  jobStatisticsData: any[]
  divisionList: string[]

  projectStat: any
  projectPileStat: any
  projectPileHolesUpdateThisWeek: any[]
  projectProgressionByMachine: any

  loading: boolean
  error: string | null

  getSuccessful: (
    startDate?: string,
    endDate?: string,
    division?: string[]
  ) => Promise<{ success: boolean; data: any[] }>
  getUnsuccessful: (
    startDate?: string,
    endDate?: string,
    division?: string[]
  ) => Promise<{ success: boolean; data: any[] }>
  getAll: (
    startDate?: string,
    endDate?: string,
    division?: string[]
  ) => Promise<{ success: boolean; data: any[] }>
  getStacked: (division?: string[]) => Promise<{ success: boolean; data: any[] }>
  getIssue: (division?: string[]) => Promise<{ success: boolean; data: any[] }>
  getStatus: (
    startDate?: string,
    endDate?: string,
    division?: string[]
  ) => Promise<{ success: boolean; data: ProjectStatusData }>
  getJobStatisticsType: (
    startDate?: string,
    endDate?: string,
    dataType?: 'month' | 'daily',
    division?: string[]
  ) => Promise<{ success: boolean; data: any[] }>
  getDivisionList: () => Promise<{ success: boolean; data: string[] }>

  getProjectStats: () => Promise<{ success: boolean; data: any }>
  getProjectPileStats: () => Promise<{ success: boolean; data: any }>
  getProjectPileHolesUpdateThisWeek: () => Promise<{ success: boolean; data: any[] }>
  getProjectProgressionByMachine: (filters?: any) => Promise<{ success: boolean; data: any[] }>

  clearState: () => void
}

export const useChartStore = create<ChartStore>(set => ({
  successfulData: [],
  unsuccessfulData: [],
  allData: [],
  stackedData: [],
  issueData: [],
  comparisonData: {
    projects: [],
    data: [],
  },
  jobStatisticsData: [],
  divisionList: [],

  projectStat: {},
  projectPileStat: {},
  projectPileHolesUpdateThisWeek: [],
  projectProgressionByMachine: {},

  loading: false,
  error: null,

  getSuccessful: async (startDate, endDate, division) => {
    set({ loading: true, error: null })
    let url = `${API_SLUG}${ENDPOINTS.SUCCESSFUL}`

    // Add date parameters if provided
    if (startDate && endDate) {
      url += `?startDate=${startDate}&endDate=${endDate}`
    }

    if (Array.isArray(division)) {
      if (division.length) {
        if (!startDate || !endDate) {
          url += '?'
        }
        division.forEach(div => {
          url += `&division[]=${div}`
        })
      }
    }

    return axios
      .get(url)
      .then(response => {
        console.log('getSuccessful response:', response.data)
        set({ successfulData: response.data ?? [], loading: false })
        return { success: true, data: response.data ?? [] }
      })
      .catch(error => {
        set({ loading: false, error: error.message })
        return { success: false, data: error?.response?.data }
      })
  },

  getUnsuccessful: async (startDate, endDate, division) => {
    set({ loading: true, error: null })
    let url = `${API_SLUG}${ENDPOINTS.UNSUCCESSFUL}`

    // Add date parameters if provided
    if (startDate && endDate) {
      url += `?startDate=${startDate}&endDate=${endDate}`
    }

    if (Array.isArray(division)) {
      if (division.length) {
        if (!startDate || !endDate) {
          url += '?'
        }
        division.forEach(div => {
          url += `&division[]=${div}`
        })
      }
    }

    return axios
      .get(url)
      .then(response => {
        console.log('getUnsuccessful response:', response.data)
        set({ unsuccessfulData: response.data ?? [], loading: false })
        return { success: true, data: response.data ?? [] }
      })
      .catch(error => {
        set({ loading: false, error: error.message })
        return { success: false, data: error?.response?.data }
      })
  },

  getAll: async (startDate, endDate, division) => {
    set({ loading: true, error: null })
    let url = `${API_SLUG}${ENDPOINTS.ALL}`

    // Add date parameters if provided
    if (startDate && endDate) {
      url += `?startDate=${startDate}&endDate=${endDate}`
    }

    if (Array.isArray(division)) {
      if (division.length) {
        if (!startDate || !endDate) {
          url += '?'
        }
        division.forEach(div => {
          url += `&division[]=${div}`
        })
      }
    }

    return axios
      .get(url)
      .then(response => {
        console.log('getAll response:', response.data)
        set({ allData: response.data ?? [], loading: false })
        return { success: true, data: response.data ?? [] }
      })
      .catch(error => {
        set({ loading: false, error: error.message })
        return { success: false, data: error?.response?.data }
      })
  },

  getStacked: async division => {
    set({ loading: true, error: null })
    return axios
      .get(`${API_SLUG}${ENDPOINTS.STACKED}`, {
        params: {
          division: division?.length ? division : undefined,
        },
      })
      .then(response => {
        set({ stackedData: response.data ?? [], loading: false })
        return { success: true, data: response.data ?? [] }
      })
      .catch(error => {
        set({ loading: false, error: error.message })
        return { success: false, data: error?.response?.data }
      })
  },

  getIssue: async division => {
    set({ loading: true, error: null })
    return axios
      .get(`${API_SLUG}${ENDPOINTS.ISSUE}`, {
        params: {
          division: division?.length ? division : undefined,
        },
      })
      .then(response => {
        console.log('getIssue response:', response.data)
        set({ issueData: response.data ?? [], loading: false })
        return { success: true, data: response.data ?? [] }
      })
      .catch(error => {
        set({ loading: false, error: error.message })
        return { success: false, data: error?.response?.data }
      })
  },

  getStatus: async (startDate, endDate, division) => {
    set({ loading: true, error: null })
    return axios
      .get(`${API_SLUG}${ENDPOINTS.STATUS}`, {
        params: {
          startDate,
          endDate,
          division: division?.length ? division : undefined,
        },
      })
      .then(response => {
        const data = response.data as ProjectStatusData
        set({ comparisonData: data ?? { projects: [], data: [] }, loading: false })
        return { success: true, data: data ?? { projects: [], data: [] } }
      })
      .catch(error => {
        set({ loading: false, error: error.message })
        return { success: false, data: { projects: [], data: [] } }
      })
  },

  getJobStatisticsType: async (startDate, endDate, dataType, division) => {
    set({ loading: true, error: null })
    return axios
      .get(`${API_SLUG}${ENDPOINTS.JOB_STATISTICS}`, {
        params: {
          startDate,
          endDate,
          dataType,
          division: division?.length ? division : undefined,
        },
      })
      .then(response => {
        set({ jobStatisticsData: response.data ?? [], loading: false })
        return { success: true, data: response.data ?? [] }
      })
      .catch(error => {
        set({ loading: false, error: error.message })
        return { success: false, data: error?.response?.data }
      })
  },

  getDivisionList: async () => {
    set({ loading: true, error: null })
    return axios
      .get(`${API_SLUG}${ENDPOINTS.DIVISION_LIST}`)
      .then(response => {
        set({ divisionList: response.data?.data ?? [], loading: false })
        return { success: true, data: response.data?.data ?? [] }
      })
      .catch(error => {
        set({ loading: false, error: error.message })
        return { success: false, data: error?.response?.data }
      })
  },

  getProjectStats: async () => {
    set({ loading: true, error: null })
    return axios
      .get(`${API_SLUG}${ENDPOINTS.PROJECT_STATS}`)
      .then(response => {
        set({ projectStat: response.data ?? {}, loading: false })
        return { success: true, data: response.data ?? {} }
      })
      .catch(error => {
        set({ loading: false, error: error.message })
        return { success: false, data: error?.response?.data }
      })
  },

  getProjectPileStats: async () => {
    set({ loading: true, error: null })
    return axios
      .get(`${API_SLUG}${ENDPOINTS.PROJECT_PILE_STATS}`)
      .then(response => {
        set({ projectPileStat: response.data ?? {}, loading: false })
        return { success: true, data: response.data ?? {} }
      })
      .catch(error => {
        set({ loading: false, error: error.message })
        return { success: false, data: error?.response?.data }
      })
  },

  getProjectPileHolesUpdateThisWeek: async () => {
    set({ loading: true, error: null })
    return axios
      .get(`${API_SLUG}${ENDPOINTS.PROJECT_PILE_HOLES_UPDATE_THIS_WEEK}`)
      .then(response => {
        set({ projectPileHolesUpdateThisWeek: response.data ?? [], loading: false })
        return { success: true, data: response.data ?? [] }
      })
      .catch(error => {
        set({ loading: false, error: error.message })
        return { success: false, data: error?.response?.data }
      })
  },

  getProjectProgressionByMachine: async (filters = {}) => {
    set({ loading: true, error: null })
    return axios
      .get(`${API_SLUG}${ENDPOINTS.PROJECT_PROGRESSION_BY_MACHINE}`, {
        params: filters,
      })
      .then(response => {
        set({ projectProgressionByMachine: response.data ?? {}, loading: false })
        return { success: true, data: response.data ?? [] }
      })
      .catch(error => {
        set({ loading: false, error: error.message })
        return { success: false, data: error?.response?.data }
      })
  },

  clearState: () => {
    set({
      successfulData: [],
      unsuccessfulData: [],
      allData: [],
      stackedData: [],
      issueData: [],
      comparisonData: {
        projects: [],
        data: [],
      },
      jobStatisticsData: [],
      loading: false,
      error: null,
    })
  },
}))
